<template>
	<div ref="map" class="col-span-full relative bg-primary-background">
		<!-- Google Map -->
		<gmaps-map
			class="h-screen-ribbon-calc lg:h-[900px] w-full asset-map"
			:markers="markerData"
			:origin="locationData"
			@on-map-mount="onMapMount"
			@on-map-error="onMapError"
		/>

		<!-- Property Card holder - absolute position -->
		<transition-group name="fade">
			<div
				v-if="!isFullscreen && activeAsset"
				class="absolute bottom-0 lg:bottom-auto lg:top-0 left-1/2 lg:left-0 -translate-x-1/2 lg:translate-x-0 max-w-md w-4/5 mb-8 lg:m-8 shadow-lg"
			>
				<component :is="`llc-mpc-event-map-cards`" :card-data="cardData" @close-card="closeProductCard" />
			</div>
		</transition-group>
		<transition-group name="fade">
			<dialog
				v-if="isFullscreen && activeAsset"
				class="fixed top-[unset] bottom-0 lg:bottom-auto lg:top-0 left-1/2 lg:left-0 -translate-x-1/2 lg:translate-x-0 max-w-md w-4/5 mb-8 lg:m-8 shadow-lg"
			>
				<component :is="`llc-mpc-event-map-cards`" :card-data="cardData" @close-card="closeProductCard" />
			</dialog>
		</transition-group>
		<div v-show="isLoading && mapMounted" class="absolute top-4 left-8 font-medium">Loading...</div>
	</div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType, reactive, SetupContext, toRefs, watch } from 'vue';
import { Cluster, defaultOnClusterClickHandler } from '@googlemaps/markerclusterer';

import { scrollToCoord } from '../../../../framework/scripts/utils/scroll';
import { GmapsMapController } from '../gmaps-map/gmaps-map-controller';

import { LlcMpcMapProperties, PropertyMapState } from './interfaces';

const useLocationMap = (props: LlcMpcMapProperties, context: SetupContext) => {
	console.log(props.cardData, props.locationData);
	const toPascalCase = (text) => {
		return text.replace(/(^\w|-\w)/g, clearAndUpper);
	};
	const clearAndUpper = (text) => {
		return text.replace(/-/, '').toUpperCase();
	};

	const appElement = document.querySelector('#app');

	// Set marker colour based on theme
	let markerColour = 'AzureBlue';
	if (appElement !== null && appElement.dataset.themeColour === '' && appElement.dataset.theme === 'aura') {
		markerColour = 'Pink';
	} else if (appElement !== null && appElement.dataset.themeColour !== '') {
		markerColour = toPascalCase(appElement.dataset.themeColour);
	}

	const state: PropertyMapState = reactive({
		activeAsset: undefined,
		map: null,
		mapController: undefined,
		mapMounted: false,
		markerData: computed(() => {
			return [
				{
					markerImage: `stockland${state.markerColour}`,
					location: props.locationData,
				},
			];
		}),
		isFullscreen: false,
		propertyCard: null,
		markerColour,
	});

	watch(
		() => props.isLoading,
		() => {
			state.activeAsset = undefined;
		}
	);
	const onMapMount = (mC: GmapsMapController) => {
		state.mapMounted = true;
		state.mapController = mC;
		// This should be moved into a handler like below
		google.maps.event.addListener(state.mapController.map, 'click', () => {
			state.mapController?.resetMarkerIcons();
			state.activeAsset = undefined;
		});
		function isInFullScreen() {
			return (
				(document.fullscreenElement ||
					(document as any).webkitFullscreenElement ||
					(document as any).mozFullScreenElement ||
					(document as any).msFullscreenElement) != null
			);
		}

		function handleFullscreenChange() {
			state.isFullscreen = isInFullScreen();
		}

		document.addEventListener('fullscreenchange', handleFullscreenChange);
		document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
		document.addEventListener('mozfullscreenchange', handleFullscreenChange);
		document.addEventListener('MSFullscreenChange', handleFullscreenChange);

		state.mapController.setMarkerClickHandler((markerData) => {
			// Set center on where the marker is
			const mc = state.mapController as GmapsMapController;
			mc.panTo(props.locationData);
			google.maps.event.addListenerOnce(mc.map, 'idle', () => {
				mc.smoothZoom(12, mc.map.getZoom() ?? 12);
			});

			mc.setAllIcons(`stockland${state.markerColour}`);
			mc.setActiveIcon(markerData, `stockland${state.markerColour}Muted`);

			// This will scroll to make sure whole map is in view for tablet
			if (state.map !== null && window.innerWidth < 769 && !state.isFullscreen) {
				scrollToCoord(state.map.getBoundingClientRect().bottom + window.innerHeight, 0);
			}
			if (state.map !== null && window.innerWidth < 769 && !state.isFullscreen) {
				scrollToCoord(window.scrollY + state.map.getBoundingClientRect().bottom - window.innerHeight, 0);
			}

			setTimeout(() => {
				let dialog = document.querySelector('dialog');
				dialog?.showModal();
				dialog?.addEventListener('click', (event) => {
					if (event.target === dialog) {
						state.mapController?.resetMarkerIcons();
					}
				});
				state.activeAsset = true;
			}, 250);
		});
		state.mapController.setClusterClickHandler(
			(event: google.maps.MapMouseEvent, cluster: Cluster, map: google.maps.Map) => {
				defaultOnClusterClickHandler(event, cluster, map);
				state.mapController?.resetMarkerIcons();
			}
		);
	};

	function onMapError(error: string) {
		console.error('Map error raised:', error);
	}

	function closeProductCard() {
		state.mapController?.resetMarkerIcons();
		state.activeAsset = undefined;
		document.querySelector('dialog')?.close();
	}

	function showListView() {
		context.emit('showListView');
	}

	return {
		state,
		onMapError,
		onMapMount,
		closeProductCard,
		showListView,
	};
};

export default defineComponent({
	name: 'llc-mpc-event-map',
	props: {
		isLoading: {
			type: Boolean,
			required: true,
		},
		cardData: {
			type: Object,
			required: true,
		},
		locationData: {
			type: { lat: Number, lng: Number },
			required: true,
		},
	},
	setup(props: LlcMpcMapProperties, context: SetupContext) {
		const { state, ...methods } = useLocationMap(props, context);
		return {
			...toRefs(state),
			...methods,
		};
	},
});
</script>
<style lang="postcss"></style>
